import React from "react";
import { Layout, Dropdown, Menu, Avatar, Space, Button, Typography, Select } from "antd";
import { useNavigate } from "react-router-dom";
import {
  UserOutlined,
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import styles from "./Header.module.scss";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../utils/api";
import ChangePasswordModal from "./ChangePasswordModal";
import { LockOutlined } from "@ant-design/icons";

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  onToggle: () => void;
  collapsed: boolean;
}

const AppHeader: React.FC<HeaderProps> = ({ onToggle, collapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = React.useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = React.useState<string | null>(null);
  const [isChangePasswordVisible, setIsChangePasswordVisible] = React.useState(false);

  // Check if user is Super Admin
  const isSuperAdmin = user?.roles?.some((role: string) =>
    role.toUpperCase() === 'SUPER_ADMIN' || role.toUpperCase() === 'SUPERADMIN'
  );

  const EMPLOYEE_ROLES = ['EMPLOYEE', 'CONTRACT_EMPLOYEE', 'INTERN'];
  const isAdmin = !user?.roles?.every((role: string) => 
    EMPLOYEE_ROLES.includes(role.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_'))
  );

  React.useEffect(() => {
    if (isSuperAdmin) {
      // Fetch tenants
      apiService.getAllTenants().then((response) => {
        if (response.success) {
          setTenants(response.data);
        }
      }).catch(err => console.error("Failed to fetch tenants", err));

      // Set initial selected tenant from localStorage
      const storedTenantId = localStorage.getItem('tenantId');
      setSelectedTenant(storedTenantId || 'all');
    }
  }, [isSuperAdmin]);

  const handleTenantChange = (value: string) => {
    setSelectedTenant(value);

    if (value === 'all') {
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenantCode');
    } else {
      const tenant = tenants.find(t => String(t.tenant_id) === value);
      if (tenant) {
        localStorage.setItem('tenantId', String(tenant.tenant_id));
        if (tenant.tenant_code) {
          localStorage.setItem('tenantCode', tenant.tenant_code);
        }
      }
    }

    // Refresh page to apply new tenant context
    window.location.reload();
  };

  const handleLogout = () => {
    logout();
  };

    const userMenuItems = [
      {
        key: "profile",
        label: "Profile",
        icon: <UserOutlined />,
        onClick: () => navigate(isAdmin ? '/admin/profile' : '/employee/profile'),
      },
      ...(isAdmin ? [{
        key: "employee-portal",
        label: "Employee Self-Service",
        icon: <UserOutlined />,
        onClick: () => navigate('/employee'),
      }] : []),
      {
        key: "changePassword",
      label: "Change Password",
      icon: <LockOutlined />,
      onClick: () => setIsChangePasswordVisible(true),
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];



  return (
    <AntHeader className={`${styles.header} ${collapsed ? styles.collapsed : ''}`}>
      <div className={styles.headerLeft}>
        {/* Toggle Sidebar Button */}
        <Button
          type="text"
          onClick={onToggle}
          className={styles.toggleButton}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          size="large"
        />


      </div>

      {/* User Menu */}
      <div className={styles.headerRight}>
        {isSuperAdmin && (
          <div style={{ marginRight: 20 }}>
            <Select
              showSearch
              style={{ width: 250 }}
              placeholder="Select Tenant"
              optionFilterProp="children"
              value={selectedTenant}
              onChange={handleTenantChange}
              filterOption={(input: string, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={[
                { value: 'all', label: 'Show All (Super Admin View)' },
                ...tenants.map(tenant => ({
                  value: String(tenant.tenant_id),
                  label: `${tenant.company_name} (${tenant.tenant_code})`
                }))
              ]}
            />
          </div>
        )}
        <Dropdown menu={{ items: userMenuItems }} trigger={["click"]} placement="bottomRight">
          <Space className={styles.userMenu} size="small">
            <Avatar
              size={40}
              src={(user as any)?.profile_picture || user?.profile_photo || user?.profilePhoto || user?.avatar}
              icon={!((user as any)?.profile_picture || user?.profile_photo || user?.profilePhoto || user?.avatar) && <UserOutlined />}
              className={styles.avatar}
            />
            <div className={styles.userInfo}>
              <Text strong className={styles.userName}>
                {(user?.first_name && user?.last_name) 
                  ? `${user.first_name} ${user.last_name}` 
                  : (user?.first_name || user?.username || user?.user_name || 'User')}
              </Text>
              <Text type="secondary" className={styles.userEmail}>
                {user?.email || user?.email_id || 'No email'}
              </Text>
            </div>
            <DownOutlined className={styles.dropdownIcon} />
          </Space>
        </Dropdown>
      </div>

      <ChangePasswordModal 
        visible={isChangePasswordVisible} 
        onClose={() => setIsChangePasswordVisible(false)} 
      />
    </AntHeader>
  );
};

export default AppHeader;
